import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, CircularProgress,
  Alert, IconButton, Tooltip, Chip, Divider
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CodeIcon from '@mui/icons-material/Code';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import axios from 'axios';
import {
  obtenerTokenPublico, buildPublicPdfUrl, buildPublicXmlUrl, buildPublicCdrUrl,
  buildMensaje, abrirWhatsApp, fmtFechaWsp, nombreTipoDocumento
} from '../utils/whatsapp';

const FORMATOS = ['A4', 'A5', 'T80'];

function PreviewMensaje({ texto }) {
  const html = texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_\n]+)_/g, '<em>$1</em>')
    .replace(/~([^~\n]+)~/g, '<s>$1</s>')
    .replace(/\n/g, '<br/>');

  return (
    <Box sx={{
      bgcolor: '#e8fce8', border: '1px solid #c8e6c9', borderRadius: 2,
      p: 1.5, fontSize: '0.82rem', lineHeight: 1.5, fontFamily: 'inherit',
      maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      color: '#1a1a1a',
    }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default function WhatsAppDialog({
  open, onClose,
  docTipo = 'comprobante', tipoDoc = '01',
  docId, clienteId,
  serie = '', numero = '',
  fecha = '', total = 0, moneda = 'PEN',
  clienteNombre = '',
  empresaNombre = '',
}) {
  const [loading, setLoading] = useState(false);
  const [telefono, setTelefono] = useState('');
  const [telefonoEdit, setTelefonoEdit] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [editandoMensaje, setEditandoMensaje] = useState(false);
  const [error, setError] = useState('');
  const [linkPdf, setLinkPdf] = useState('');
  const [nombreEmpresa, setNombreEmpresa] = useState(empresaNombre || '');
  const [formato, setFormato] = useState('A4');
  const [tokenPdf, setTokenPdf] = useState('');
  const [plantillaTexto, setPlantillaTexto] = useState('');
  const [baseVars, setBaseVars] = useState({});
  const [copiado, setCopiado] = useState(false);
  const [adjXml, setAdjXml] = useState(false);
  const [adjCdr, setAdjCdr] = useState(false);
  const [tokenXml, setTokenXml] = useState('');
  const [tokenCdr, setTokenCdr] = useState('');
  const [linkXml, setLinkXml] = useState('');
  const [linkCdr, setLinkCdr] = useState('');

  const reconstruirLink = useCallback((nuevoFormato, token) => {
    if (!token || !docId) return '';
    return buildPublicPdfUrl(docTipo, docId, token, nuevoFormato);
  }, [docTipo, docId]);

  const reconstruirMensaje = (fmt, xmlActivo, cdrActivo) => {
    if (!plantillaTexto) return;
    const newLink = reconstruirLink(fmt, tokenPdf);
    setLinkPdf(newLink);
    const newVars = {
      ...baseVars,
      link_pdf: newLink,
      link_xml: xmlActivo && linkXml ? linkXml : '',
      link_cdr: cdrActivo && linkCdr ? linkCdr : '',
    };
    let msg = buildMensaje(plantillaTexto, newVars);

    // Construir sufijo de adjuntos
    let adjSuffix = '';
    if (xmlActivo && linkXml && !plantillaTexto.includes('{{link_xml}}')) {
      adjSuffix += `\n🗎 *XML firmado:*\n${linkXml}`;
    }
    if (cdrActivo && linkCdr && !plantillaTexto.includes('{{link_cdr}}')) {
      adjSuffix += `\n📦 *CDR SUNAT:*\n${linkCdr}`;
    }

    if (adjSuffix) {
      // Insertar justo después del link del PDF (no al final del mensaje)
      const pdfIdx = newLink ? msg.indexOf(newLink) : -1;
      if (pdfIdx !== -1) {
        const after = pdfIdx + newLink.length;
        msg = msg.substring(0, after) + adjSuffix + msg.substring(after);
      } else {
        msg += adjSuffix;
      }
    }
    setMensaje(msg);
  };

  const handleFormatoChange = (nuevoFormato) => {
    setFormato(nuevoFormato);
    if (!tokenPdf || !plantillaTexto) return;
    reconstruirMensaje(nuevoFormato, adjXml, adjCdr);
  };

  const handleToggleXml = () => {
    const next = !adjXml;
    setAdjXml(next);
    reconstruirMensaje(formato, next, adjCdr);
  };

  const handleToggleCdr = () => {
    const next = !adjCdr;
    setAdjCdr(next);
    reconstruirMensaje(formato, adjXml, next);
  };

  const copiarMensaje = () => {
    navigator.clipboard.writeText(mensaje).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }).catch(() => {
      const el = document.createElement('textarea');
      el.value = mensaje;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };

  const inicializar = useCallback(async () => {
    if (!open || !docId) return;
    setLoading(true);
    setError('');
    setEditandoMensaje(false);
    setTelefonoEdit(false);
    setCopiado(false);
    setFormato('A4');
    setAdjXml(false);
    setAdjCdr(false);
    setTokenXml('');
    setTokenCdr('');
    setLinkXml('');
    setLinkCdr('');

    try {
      // 1. Token público para el PDF
      const token = await obtenerTokenPublico(docTipo, docId);
      setTokenPdf(token || '');

      // Tokens XML/CDR (comprobantes electrónicos y guías de remisión, no cotizaciones)
      if (docTipo === 'comprobante' || docTipo === 'guia') {
        const tipoXml = docTipo === 'guia' ? 'xml-guia' : 'xml';
        const tipoCdr = docTipo === 'guia' ? 'cdr-guia' : 'cdr';
        try {
          const [rxm, rcd] = await Promise.all([
            axios.get(`/api/wsp-config/generar-token/${tipoXml}/${docId}`),
            axios.get(`/api/wsp-config/generar-token/${tipoCdr}/${docId}`),
          ]);
          setTokenXml(rxm.data.token || '');
          setTokenCdr(rcd.data.token || '');
          setLinkXml(rxm.data.token ? buildPublicXmlUrl(docId, rxm.data.token, docTipo) : '');
          setLinkCdr(rcd.data.token ? buildPublicCdrUrl(docId, rcd.data.token, docTipo) : '');
        } catch (_) {}
      }
      const url = token ? buildPublicPdfUrl(docTipo, docId, token, 'A4') : '';
      setLinkPdf(url);

      // 2. Teléfono del cliente
      let tel = '';
      let nombreCliente = clienteNombre || '';
      if (clienteId) {
        try {
          const r = await axios.get(`/api/wsp-config/telefono-cliente/${clienteId}`);
          tel = r.data.telefono || '';
          if (!nombreCliente && r.data.nombre) nombreCliente = r.data.nombre;
        } catch (_) {}
      }
      setTelefono(tel);
      setTelefonoEdit(!tel);

      // 3. Nombre empresa
      let empNombre = empresaNombre || nombreEmpresa;
      if (!empNombre) {
        try {
          const ec = await axios.get('/api/empresa-config');
          empNombre = ec.data?.razon_social || ec.data?.nombre_comercial || '';
        } catch (_) {}
      }
      setNombreEmpresa(empNombre);

      // 4. Plantilla
      const pt = await axios.get(`/api/wsp-config/plantilla/${tipoDoc}`);
      const txtPlantilla = pt.data?.plantilla_texto || '';
      setPlantillaTexto(txtPlantilla);

      // 5. Variables base
      const vars = {
        nombre_empresa: empNombre,
        nombre_cliente: nombreCliente,
        ruc_cliente: '',
        tipo_documento_nombre: nombreTipoDocumento(tipoDoc),
        serie: serie,
        numero: String(numero).padStart(8, '0'),
        fecha: fmtFechaWsp(fecha),
        total: parseFloat(total || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
        moneda: ({ USD: '$', EUR: '€', PEN: 'S/' })[moneda] || 'S/',
        link_pdf: url || '(link disponible al abrir)',
        link_xml: '',
        link_cdr: '',
      };
      setBaseVars({ ...vars, link_pdf: undefined, link_xml: undefined, link_cdr: undefined });
      setMensaje(buildMensaje(txtPlantilla, vars));
    } catch (e) {
      setError('Error al cargar datos: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [open, docId, docTipo, tipoDoc, clienteId, clienteNombre, serie, numero, fecha, total, moneda, empresaNombre]);

  useEffect(() => { inicializar(); }, [inicializar]);

  const handleEnviar = () => {
    if (!mensaje.trim()) return;
    abrirWhatsApp(telefono, mensaje);
  };

  const numeroFormateado = `${serie}-${String(numero).padStart(8, '0')}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <WhatsAppIcon sx={{ color: '#25d366', fontSize: 28 }} />
        <Box>
          <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
            Enviar por WhatsApp
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {nombreTipoDocumento(tipoDoc)} {numeroFormateado}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#25d366' }} />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

            {/* Teléfono */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                NÚMERO WHATSAPP DEL DESTINATARIO
              </Typography>
              {telefonoEdit || !telefono ? (
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <TextField
                    size="small" fullWidth autoFocus
                    placeholder="Ej: 987654321 o 51987654321"
                    value={telefono}
                    onChange={e => setTelefono(e.target.value.replace(/\s/g, ''))}
                    InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 0.5, color: '#25d366', fontSize: 18 }} /> }}
                    helperText="Sin espacios ni guiones. Con código país: 51987654321"
                  />
                  {telefono && (
                    <Tooltip title="Confirmar">
                      <IconButton size="small" onClick={() => setTelefonoEdit(false)} sx={{ color: '#25d366' }}>
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip icon={<PhoneIcon />} label={telefono} color="success" variant="outlined" size="small" />
                  <Tooltip title="Cambiar número">
                    <IconButton size="small" onClick={() => setTelefonoEdit(true)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Formato PDF + Adjuntos */}
            {linkPdf && (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <PictureAsPdfIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    FORMATO Y ADJUNTOS
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                  {FORMATOS.map(f => (
                    <Chip
                      key={f}
                      label={f}
                      size="small"
                      onClick={() => handleFormatoChange(f)}
                      color={formato === f ? 'primary' : 'default'}
                      variant={formato === f ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer', fontWeight: formato === f ? 'bold' : 'normal', minWidth: 44 }}
                    />
                  ))}
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Chip
                    icon={<PictureAsPdfIcon />}
                    label="PDF"
                    size="small"
                    color="error"
                    variant="filled"
                    sx={{ cursor: 'default', fontWeight: 'bold' }}
                  />
                  {(docTipo === 'comprobante' || docTipo === 'guia') && tokenXml && (
                    <Chip
                      icon={<CodeIcon />}
                      label="XML"
                      size="small"
                      onClick={handleToggleXml}
                      color={adjXml ? 'success' : 'default'}
                      variant={adjXml ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                  {(docTipo === 'comprobante' || docTipo === 'guia') && tokenCdr && (
                    <Chip
                      icon={<FolderZipIcon />}
                      label="CDR"
                      size="small"
                      onClick={handleToggleCdr}
                      color={adjCdr ? 'success' : 'default'}
                      variant={adjCdr ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                    {formato === 'T80' ? '(térmico)' : formato === 'A5' ? '(A5)' : '(carta)'}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Mensaje */}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  MENSAJE
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title={copiado ? '¡Copiado!' : 'Copiar mensaje'}>
                    <IconButton size="small" onClick={copiarMensaje} sx={{ color: copiado ? '#25d366' : 'inherit' }}>
                      {copiado ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={editandoMensaje ? 'Ver preview' : 'Editar mensaje'}>
                    <IconButton size="small" onClick={() => setEditandoMensaje(v => !v)}>
                      <EditIcon fontSize="small" color={editandoMensaje ? 'primary' : 'inherit'} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {editandoMensaje ? (
                <TextField
                  multiline rows={8} fullWidth size="small"
                  value={mensaje}
                  onChange={e => setMensaje(e.target.value)}
                  placeholder="Escribe el mensaje..."
                  inputProps={{ style: { fontSize: '0.82rem', lineHeight: 1.5, fontFamily: 'inherit' } }}
                  helperText="*negrita*  _cursiva_  ~tachado~  Emojis: cópialos y pégalos directo"
                />
              ) : (
                <PreviewMensaje texto={mensaje} />
              )}
            </Box>

            {linkPdf && (
              <Alert severity="info" sx={{ py: 0.5, mt: 1 }}>
                <Typography variant="caption">
                  El destinatario descargará el PDF en formato <strong>{formato}</strong> sin necesitar cuenta.
                  {(adjXml || adjCdr) && (
                    <> Los links de {[adjXml && 'XML', adjCdr && 'CDR'].filter(Boolean).join(' y ')} se incluirán en el mensaje.</>
                  )}
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button
          variant="outlined"
          startIcon={copiado ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={copiarMensaje}
          sx={{ color: copiado ? '#25d366' : 'inherit', borderColor: copiado ? '#25d366' : 'inherit' }}
        >
          {copiado ? 'Copiado' : 'Copiar'}
        </Button>
        <Button
          variant="contained"
          startIcon={<WhatsAppIcon />}
          onClick={handleEnviar}
          disabled={loading || !mensaje.trim()}
          sx={{ bgcolor: '#25d366', '&:hover': { bgcolor: '#1ebe59' } }}
        >
          Abrir WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
}
